import { Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { RemoveProgramParticipantsDialogComponent } from 'app/library/course-offering-participants/components/dialogs/remove-program-participants-dialog/remove-program-participants-dialog.component';
import { CourseOfferingParticipantsService } from 'app/library/course-offering-participants/services/course-offering-participants.service';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { AddUsersToModulesAsManagers, AddUsersToModulesAsManagersSuccess, RemoveUsersFromModulesAsManagers, UpdateModuleManagerPermissions } from 'app/library/module-user/actions';
import { ModuleUserExtended } from 'app/library/module-user/models/module-user.extended.model';
import { ModuleUserState } from 'app/library/module-user/models/module-user.state';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { MyModulesState } from 'app/library/my-profile-modules/models/my-modules.state';
import { FetchTemplateManagers } from 'app/library/template-managers/actions';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmNotifyDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm-notify/confirm-notify.component';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { NotifyType } from 'app/projects/shared/src/lib/enums/notify-type.enum';
import { IConfirmNotifyDialogResult } from 'app/projects/shared/src/lib/interfaces/confirm-dialog-result.interface';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { UsersState } from 'app/projects/user/src/lib/models/users.state';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { FeatureService } from '../../../feature/services/feature.service';
import { ModuleUser } from '../../../module-user/models/module-user.model';
import { Module } from '../../../module/models/module.model';
import { DeselectSelectedModuleManagers, FetchModuleManagers, SearchModuleManagers, SelectFilteredModuleManagers, ToggleSelectedModuleManager } from '../../actions';
import { ModuleManagersState } from '../../models/module-managers.state';
import { ModuleManagersService } from '../../services/module-managers.service';
import { AddManagersDialogComponent } from '../dialogs/add-managers-dialog/add-managers-dialog.component';
import { AddProgramManagersDialogComponent } from '../dialogs/add-program-managers-dialog/add-program-managers-dialog.component';
import { EditManagerPermissionsDialogComponent } from '../dialogs/edit-manager-permissions-dialog/edit-manager-permissions-dialog.component';

@Component({
    selector: 'app-module-manager-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ModuleManagerListComponent extends UnsubscribeOnDestroy implements OnInit, OnDestroy {
    @Input()
    module: Module;

    managers: ModuleUser[] = [];

    @Select(ModuleManagersState.getEntities)
    manager$: Observable<ModuleUser[]>;

    @Select(ModuleManagersState.getFilteredEntities)
    filteredManager$: Observable<ModuleUser[]>;

    @Select(ModuleManagersState.getSelectedEntityIds)
    selectedManagerId$: Observable<string[]>;

    users: User[];

    @Select(ModuleUserState.getEntities)
    moduleUser$: Observable<ModuleUser[]>;
    moduleUsers: ModuleUser[];

    @Select(UserState.getEntities)
    user$: Observable<User[]>;

    @Select(AllUsersState.getEntities)
    allUser$: Observable<User[]>;

    @Select(UsersState.getFilteredEntities)
    filteredUser$: Observable<User[]>;

    profile = this._store.selectSnapshot(UserState.getMyInfo);

    @Select(MyModulesState.getEntities)
    myModuleUser$: Observable<ModuleUser[]>;
    moduleUser: ModuleUser;

    @ViewChild(MatSort, { static: true })
    sort: MatSort;

    dataSource: MatTableDataSource<ModuleUserExtended>;
    displayedColumns = ['checkbox', 'avatar', 'fullName', 'email', 'buttons'];
    selectedManagerIds: string[] = [];
    checkboxes: {};
    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    confirmNotifyDialogRef: MatDialogRef<MaxBrainConfirmNotifyDialogComponent>;
    addProgramManagersByDialogRef: MatDialogRef<AddProgramManagersDialogComponent>;
    removeProgramParticipantsByDialogRef: MatDialogRef<RemoveProgramParticipantsDialogComponent>;

    selectedParticipantIds: string[] = [];

    searchInput = new FormControl(this._store.selectSnapshot(ModuleManagersState).searchTerm);
    hideEmail = false;
    ModuleKind = ModuleKind;
    hasPermissions: boolean = false;

    constructor(
        private _store: Store,
        public _matDialog: MatDialog,
        private _featureService: FeatureService,
        private _moduleManagersService: ModuleManagersService,
        private _translateService: TranslateService,
        private _matSnackBar: MatSnackBar,
        private _action$: Actions,
        private _courseOfferingParticipantsService: CourseOfferingParticipantsService
    ) {
        super();

        this.hideEmail = this._featureService.checkFeatureStatus(FeatureSwitchName.ParticipantEmailVisibility, 'inactive');
    }

    private _setDataSource(): void {
        if (this.managers && this.users) {
            if (!this.dataSource) {
                this.dataSource = new MatTableDataSource(this.managers as ModuleUserExtended[]);
                this.dataSource.sort = this.sort;
            } else {
                this.dataSource.data = this.managers as ModuleUserExtended[];
            }
        }
    }

    get hasUsersViewPermission(): boolean {
        return this.profile.permissions.includes(AdminPermissions.USERS_READ);
    }

    get hasTenantAdminPermission(): boolean {
        return this.profile.permissions.includes(AdminPermissions.TENANT_MANAGEMENT);
    }

    ngOnInit(): void {
        this.hasPermissions = this.hasUsersViewPermission || this.hasTenantAdminPermission;

        if (!this.hasPermissions) {
            this.displayedColumns = ['avatar', 'fullName', 'email'];
        }

        this.myModuleUser$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((myModuleUsers) => !!myModuleUsers)
            )
            .subscribe((myModuleUsers) => {
                if (this.module.id) {
                    this.moduleUser = myModuleUsers.filter((tempModuleUser) => tempModuleUser.moduleId === this.module.id)[0];
                }
            });

        this.filteredManager$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((managers) => !!managers)
            )
            .subscribe((managers) => {
                this.managers = managers;

                this._setDataSource();
            });

        this.user$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((users) => !!users)
            )
            .subscribe((users) => {
                this.users = users;

                this._setDataSource();
            });

        this.manager$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((managers) => !!managers)
            )
            .subscribe((managers) => {
                this.managers = managers;

                this.checkboxes = {};

                managers.map((manager) => {
                    this.checkboxes[manager.id] = this.selectedManagerIds.includes(manager.id);
                });
            });

        this.moduleUser$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((moduleUsers) => !!moduleUsers)
            )
            .subscribe((moduleUsers) => {
                this.moduleUsers = moduleUsers;

                this._setDataSource();
            });

        this.selectedManagerId$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((userIds) => !!userIds)
            )
            .subscribe((selectedUserIds) => {
                for (const id in this.checkboxes) {
                    if (this.checkboxes.hasOwnProperty(id)) {
                        this.checkboxes[id] = selectedUserIds.includes(id);
                    }
                }

                this.selectedManagerIds = selectedUserIds;
            });

        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(300), distinctUntilChanged()).subscribe((searchTerm) => {
            this._store.dispatch(new SearchModuleManagers(searchTerm));
        });
    }

    openAddManagersDialog(): void {
        if (this.module.kind === this.ModuleKind.ProgramExecution) {
            this.addBookings();
        } else {
            this.openSelectManagersDialog();
        }
    }

    addBookings(): void {
        this.addProgramManagersByDialogRef = this._matDialog.open(AddProgramManagersDialogComponent, {
            width: '668px',
        });

        this.addProgramManagersByDialogRef.componentInstance.entitie$ = this.allUser$;
        this.addProgramManagersByDialogRef.componentInstance.entityId = this.module.id;
        this.addProgramManagersByDialogRef.componentInstance.dataType = 'users';
        this.addProgramManagersByDialogRef.componentInstance.list = [];
        this.addProgramManagersByDialogRef.componentInstance.title = 'GENERAL.LABEl.CHOOSE_MANAGERS';

        this.addProgramManagersByDialogRef.afterClosed().subscribe(async (result: { userList: User[]; moduleList: string[] }) => {
            if (result && result.userList) {
                this._store.dispatch(
                    new AddUsersToModulesAsManagers({
                        userIds: result.userList.map((user) => user.id),
                        moduleIds: [...result.moduleList, this.module.id],
                        permissions: ModuleUser.getAllPermissions(),
                    })
                );

                this._action$.pipe(takeUntil(this._unsubscribeAll), ofActionDispatched(AddUsersToModulesAsManagersSuccess)).subscribe(() => {
                    this._moduleManagersService.fetchManagersByModuleId(this.module.id);
                });
            }

            this.addProgramManagersByDialogRef = null;
        });
    }

    openSelectManagersDialog(): void {
        const dialogRef = this._matDialog.open(AddManagersDialogComponent, {
            width: '668px',
            id: 'select-users-dialog',
        });

        dialogRef.componentInstance.entitie$ = this.allUser$;
        dialogRef.componentInstance.entityId = this.module.id;
        dialogRef.componentInstance.title = 'GENERAL.LABEl.CHOOSE_MANAGERS';
        dialogRef.componentInstance.dataType = 'users';
        dialogRef.componentInstance.role = 'moduleManager';

        dialogRef.afterClosed().subscribe((result: { list: User[]; permissions: string[] }) => {
            if (result && result.list) {
                this._store.dispatch(
                    new AddUsersToModulesAsManagers({
                        userIds: result.list.map((user) => user.id),
                        moduleIds: [this.module.id],
                        permissions: result.permissions,
                    })
                );

                this._action$.pipe(takeUntil(this._unsubscribeAll), ofActionDispatched(AddUsersToModulesAsManagersSuccess)).subscribe(() => {
                    const successMessage = this._translateService.instant('ADD_MANAGER.DIALOG.MESSAGE.SUCCEESS', {
                        usersCount: result.list.length,
                    });

                    this._matSnackBar.open(successMessage, 'OK', {
                        verticalPosition: 'top',
                        duration: SnackBarTime.medium,
                    });

                    this._moduleManagersService.fetchManagersByModuleId(this.module.id);
                });
            }
        });
    }

    editManager(manager: ModuleUserExtended): void {
        this._store.dispatch(new Navigate([`/${User.label_plural}/${manager.user.id}/edit`]));
    }

    editManagerPermissions(manager: ModuleUserExtended): void {
        const dialogRef = this._matDialog.open(EditManagerPermissionsDialogComponent, {
            disableClose: false,
            width: '632px',
            autoFocus: false,
        });

        dialogRef.componentInstance.title = this._translateService.instant('GENERAL.LABEL.EDIT_MANAGER_PERMISSIONS_FOR', {
            fullName: manager.user.fullName,
        });

        dialogRef.componentInstance.selectedPermissions = manager.permissions;

        dialogRef.afterClosed().subscribe(
            (result: {
                data: {
                    permissions: string[];
                };
            }) => {
                if (result && result.data) {
                    this._store.dispatch(
                        new UpdateModuleManagerPermissions({
                            userId: manager.userId,
                            moduleId: manager.moduleId,
                            permissions: result.data.permissions,
                        })
                    );
                }
            }
        );
    }

    deleteSelected(manager?: ModuleUserExtended): void {
        this.confirmNotifyDialogRef = this._matDialog.open(MaxBrainConfirmNotifyDialogComponent, {
            disableClose: false,
            width: '600px',
        });

        this.confirmNotifyDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        this.confirmNotifyDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmNotifyDialogRef.componentInstance.confirmMessage = this._translateService.instant(
            this.module.kind === ModuleKind.ProgramExecution ? 'GENERAL.LABEL.REMOVE_MANAGERS_FROM_PROGRAM' : 'GENERAL.LABEL.REMOVE_MANAGERS_FROM_COURSE',
            {
                usersCount: this.selectedManagerIds.length || 1,
                moduleName: this.module.name,
            }
        );

        this.confirmNotifyDialogRef.componentInstance.actionButtonText = this._translateService.instant('GENERAL.BUTTON.REMOVE_USERS', {
            usersCount: this.selectedManagerIds.length || 1,
        });

        this.confirmNotifyDialogRef.afterClosed().subscribe(async (confirmResult: IConfirmNotifyDialogResult) => {
            if (confirmResult.result) {
                const userIds = this.moduleUsers
                    .map((moduleUser) => {
                        if (this.selectedManagerIds.includes(moduleUser.id)) {
                            return moduleUser.userId;
                        }
                    })
                    .filter((userId) => userId);
                const programUserIds = userIds.length ? userIds : [manager.userId];

                if (this.module.kind === ModuleKind.ProgramExecution) {
                    // if there is no participant bookings, unbook from program
                    const managerBookings = await this._courseOfferingParticipantsService.fetchManagerBookings({
                        userIds: programUserIds,
                        programExecutionId: this.module.id,
                    });

                    if (managerBookings.length) {
                        this.removeProgramParticipantsByDialogRef = this._matDialog.open(RemoveProgramParticipantsDialogComponent, {
                            disableClose: false,
                            width: '882px',
                            autoFocus: false,
                        });

                        this.removeProgramParticipantsByDialogRef.componentInstance.title = this._translateService.instant('PAGE.DIALOG.REMOVE_PROGRAM_MANAGERS.TITLE', {
                            usersCount: this.selectedManagerIds.length || 1,
                        });

                        this.removeProgramParticipantsByDialogRef.componentInstance.program = this.module;
                        this.removeProgramParticipantsByDialogRef.componentInstance.participantBookings = managerBookings;

                        this.removeProgramParticipantsByDialogRef.afterClosed().subscribe(async (result: { bookings: { user_id: string; execution_id: string }[] }) => {
                            if (result) {
                                await this.unbookManagersFromProgram(programUserIds, result.bookings, confirmResult.isNotifyChecked);
                                this.removeProgramParticipantsByDialogRef = null;
                            }
                        });
                    } else {
                        await this.unbookManagersFromProgram(programUserIds, [], confirmResult.isNotifyChecked);
                    }

                    this.confirmNotifyDialogRef = null;
                } else {
                    this._store.dispatch(
                        new RemoveUsersFromModulesAsManagers({
                            userIds: programUserIds,
                            moduleIds: [this.module.id],
                        })
                    );
                }

                this._action$.pipe(takeUntil(this._unsubscribeAll), ofActionDispatched(RemoveUsersFromModulesAsManagers)).subscribe(() => {
                    if (this.module.kind !== ModuleKind.Template && this.module.kind !== ModuleKind.ProgramTemplate) {
                        this._store.dispatch(new FetchModuleManagers(this.module.id));
                    } else {
                        this._store.dispatch(new FetchTemplateManagers({ moduleId: this.module.id }));
                    }
                });
            }

            this.confirmNotifyDialogRef = null;
        });
    }

    private async unbookManagersFromProgram(userIds: string[], bookings: { user_id: string; execution_id: string }[] = [], notify?: NotifyType): Promise<void> {
        await this._courseOfferingParticipantsService.unbookManager({
            bookings,
            userIds,
            removeFromProgram: true,
            programExecutionId: this.module.id,
            notify,
        });

        //MX-5020 - Whole Program progress stuff is done as part of the async job (ProgramUpdatesMessage)
        setTimeout(() => {
            if (this.module.kind !== ModuleKind.Template && this.module.kind !== ModuleKind.ProgramTemplate) {
                this._store.dispatch(new FetchModuleManagers(this.module.id));
            } else {
                this._store.dispatch(new FetchTemplateManagers({ moduleId: this.module.id }));
            }
        }, 3000);
    }

    onSelectedChange(id: string): void {
        this._store.dispatch(new ToggleSelectedModuleManager(id));
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected(): boolean {
        return this.selectedManagerIds.length === this.dataSource.data.length;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle(): void {
        this._store.dispatch(this.isAllSelected() ? new DeselectSelectedModuleManagers() : new SelectFilteredModuleManagers());
    }
}
